// Per-creative composition load status, published by CompositionEditorProvider and read by
// ComposerShell. Lets the mobile composer decide — synchronously, without a probe round-trip —
// whether to show the touch editor, a neutral loading state, or the legacy preview. This kills
// the flash of the old screen that a useState+effect probe caused (it lagged the async fetch, so
// the old preview painted for a beat before the editor swapped in).

import { useEffect } from 'react'
import { create } from 'zustand'

import { fetchComposition } from '../../api/composition-api'

export type CompositionStatus = 'loading' | 'canvas' | 'none'
export type CompositionKind = 'static' | 'carousel' | 'video'

interface CompositionStatusStore {
  byId: Record<string, CompositionStatus>
  /** The SHAPE of the loaded composition — lets ComposerShell route the tap on the media
   *  region to the right mobile editor (image / carousel deck / reel timeline). */
  kindById: Record<string, CompositionKind>
  set: (creativeId: string, status: CompositionStatus) => void
  setKind: (creativeId: string, kind: CompositionKind) => void
}

export const useCompositionStatus = create<CompositionStatusStore>(set => ({
  byId: {},
  kindById: {},
  set: (creativeId, status) =>
    set(st =>
      st.byId[creativeId] === status ? st : { byId: { ...st.byId, [creativeId]: status } }
    ),
  setKind: (creativeId, kind) =>
    set(st =>
      st.kindById[creativeId] === kind ? st : { kindById: { ...st.kindById, [creativeId]: kind } }
    ),
}))

/**
 * Does this POST have an editable composition? Tab-independent, unlike `useComposerEditor()`:
 * a platform tab mounts the editor provider disabled (a platform view is a read-only preview),
 * so the composer chrome can't ask the editor context whether the bar belongs on screen — it
 * asks here. Lets the control strip hold its height across a Post ⇄ platform tab switch instead
 * of collapsing and shoving the creative up the page.
 *
 * Nothing loaded the status yet (deep link straight onto a platform tab)? Probe once — a plain
 * read, none of the provider's normalize/autosave writes. 'loading' counts as present: composition
 * posts are the norm, so reserving the space up front also kills the jump on first paint.
 */
export function usePostHasComposition(creativeId: string): boolean {
  const status = useCompositionStatus(s => s.byId[creativeId])
  useEffect(() => {
    if (status) return
    let cancelled = false
    fetchComposition(creativeId)
      .then(res => {
        if (!cancelled)
          useCompositionStatus.getState().set(creativeId, res.composition ? 'canvas' : 'none')
      })
      .catch(() => {
        if (!cancelled) useCompositionStatus.getState().set(creativeId, 'none')
      })
    return () => {
      cancelled = true
    }
  }, [creativeId, status])
  return status === 'canvas' || status === 'loading'
}
